import { api } from '@/api/axios-client'
import { createInjectionState } from '@vueuse/core'
import FilePreviewDialog from '@/components/preview/FilePreviewDialog.vue'

const MIME_TYPES = {
  'aac': 'audio/aac',
  'abw': 'application/x-abiword',
  'arc': 'application/x-freearc',
  'avi': 'video/x-msvideo',
  'azw': 'application/vnd.amazon.ebook',
  'bin': 'application/octet-stream',
  'bmp': 'image/bmp',
  'bz': 'application/x-bzip',
  'bz2': 'application/x-bzip2',
  'cda': 'application/x-cdf',
  'csh': 'application/x-csh',
  'css': 'text/css',
  'csv': 'text/csv',
  'doc': 'application/msword',
  'docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'eot': 'application/vnd.ms-fontobject',
  'epub': 'application/epub+zip',
  'gz': 'application/gzip',
  'gif': 'image/gif',
  'htm': 'text/html',
  'html': 'text/html',
  'ico': 'image/vnd.microsoft.icon',
  'ics': 'text/calendar',
  'jar': 'application/java-archive',
  'jpeg': 'image/jpeg',
  'jpg': 'image/jpeg',
  'js': 'text/javascript',
  'json': 'application/json',
  'jsonld': 'application/ld+json',
  'mid': 'audio/midi audio/x-midi',
  'midi': 'audio/midi audio/x-midi',
  'mjs': 'text/javascript',
  'mp3': 'audio/mpeg',
  'mp4': 'video/mp4',
  'mpeg': 'video/mpeg',
  'odp': 'application/vnd.oasis.opendocument.presentation',
  'ods': 'application/vnd.oasis.opendocument.spreadsheet',
  'odt': 'application/vnd.oasis.opendocument.text',
  'oga': 'audio/ogg',
  'ogv': 'video/ogg',
  'ogx': 'application/ogg',
  'opus': 'audio/opus',
  'otf': 'font/otf',
  'png': 'image/png',
  'pdf': 'application/pdf',
  'php': 'application/x-httpd-php',
  'ppt': 'application/vnd.ms-powerpoint',
  'pptx':
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'rar': 'application/vnd.rar',
  'rtf': 'application/rtf',
  'sh': 'application/x-sh',
  'svg': 'image/svg+xml',
  'swf': 'application/x-shockwave-flash',
  'tar': 'application/x-tar',
  'tif': 'image/tiff',
  'tiff': 'image/tiff',
  'ts': 'video/mp2t',
  'ttf': 'font/ttf',
  'txt': 'text/plain',
  'vsd': 'application/vnd.visio',
  'wav': 'audio/wav',
  'weba': 'audio/webm',
  'webm': 'video/webm',
  'webp': 'image/webp',
  'woff': 'font/woff',
  'woff2': 'font/woff2',
  'xhtml': 'application/xhtml+xml',
  'xls': 'application/vnd.ms-excel',
  'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'xml': 'application/xml',
  'xul': 'application/vnd.mozilla.xul+xml',
  'zip': 'application/zip',
  '3gp': 'video/3gpp',
  '3g2': 'video/3gpp2',
  '7z': 'application/x-7z-compressed',
}

function createFilePreview() {
  const previewDialog = ref<InstanceType<typeof FilePreviewDialog>>()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function setPreviewDialogRef(ref: any) {
    previewDialog.value = ref
  }

  function extractFileExt(filename: string) {
    return filename.split('.').at(-1)?.toLowerCase() || 'png'
  }

  function getMimeTypeForFilename(src: string) {
    return MIME_TYPES[extractFileExt(src)] || MIME_TYPES.bin
  }

  function extractFileName(src: string) {
    // in the case where the `src` doesn't have a host the URL constructor
    // will throw an error since there is no host to parse. We're adding
    // a virtual host so that URL parsing works.
    if (src.indexOf('://') === -1) src = `https://virtual/${src}`
    const url = new URL(src)
    // Decode URL-encoded characters (e.g. %20 for space)
    return decodeURIComponent(url.pathname.split('/').at(-1) || 'image.png')
  }

  function preview(
    src: string,
    filename = extractFileName(src),
    mimeType = getMimeTypeForFilename(filename),
  ) {
    return previewDialog.value?.showModal(src, mimeType, filename)
  }

  function previewDeferred(
    loadSrc: () => Promise<string>,
    filename: string,
    mimeType = getMimeTypeForFilename(filename),
  ) {
    return previewDialog.value?.showModalDeferred(loadSrc, mimeType, filename)
  }

  // function needsConversion(mimeType: string) {
  //   const NEEDS_PREVIEW_URL_MIME_TYPES = [
  //     MIME_TYPES.MIME_TYPE_DOC,
  //     MIME_TYPES.MIME_TYPE_DOCX,
  //     MIME_TYPES.MIME_TYPE_XLS,
  //     MIME_TYPES.MIME_TYPE_XLSX,
  //     MIME_TYPES.MIME_TYPE_PPT,
  //     MIME_TYPES.MIME_TYPE_PPTX,
  //   ]

  //   return NEEDS_PREVIEW_URL_MIME_TYPES.includes(mimeType)
  // }

  // TODO: implement once preview backend exists
  // async function convertToPDF(....) { ... }

  async function previewProtected(
    src: string,
    filename = extractFileName(src),
    mimeType = getMimeTypeForFilename(filename),
    {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onDataCollected = (_dataUrl: string) => {},
    } = {},
  ) {
    const { data } = await api.get(src, { responseType: 'blob' })
    const url = URL.createObjectURL(data)
    try {
      // TODO: Currently the way to get a preview as PDF one needs to have a node id from deepbox
      //       Since we're operating on files here we need a way to ask for a preview URL.
      //       Maybe that already exists - need to ask backend guy
      // const content = needsConversion(file.content_type) ? await convertToPDF(file) : data

      onDataCollected(url)
      await preview(url, filename, mimeType)
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  type PreviewListItem = {
    src: string
    filename?: string
    mimeType?: string
  }

  async function previewProtectedList(
    items: PreviewListItem[],
    startIndex: number,
    { page }: { page?: number } = {},
  ) {
    let currentUrl: string | null = null

    async function resolveItem(index: number) {
      if (currentUrl) URL.revokeObjectURL(currentUrl)

      const item = items[index]
      const { data } = await api.get(item.src, { responseType: 'blob' })
      currentUrl = URL.createObjectURL(data)
      const filename = item.filename || extractFileName(item.src)
      return {
        src: currentUrl,
        filename,
        mimeType: item.mimeType || getMimeTypeForFilename(filename),
      }
    }

    try {
      await previewDialog.value?.showModalWithNavigation(
        resolveItem,
        startIndex,
        items.length,
        page,
      )
    } finally {
      if (currentUrl) URL.revokeObjectURL(currentUrl)
    }
  }

  return {
    setPreviewDialogRef,
    preview,
    previewDeferred,
    previewProtected,
    previewProtectedList,
  }
}

const [provideFilePreview, useFilePreviewOrUndefined] =
  createInjectionState(createFilePreview)

function useFilePreview() {
  const result = useFilePreviewOrUndefined()
  if (!result)
    throw new Error('File preview not provided. Call provideFilePreview first!')

  return result
}

export { provideFilePreview, useFilePreview }
